import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ChevronRight, Map, MessageSquare, Handshake, FileSignature, Calendar, BarChart3 } from 'lucide-react';
export function ProductOverviewPage() {
  return <div className="pt-32 pb-20" data-id="element-6728">
      {/* Hero */}
      <section className="max-w-4xl mx-auto px-6 text-center mb-24" data-id="element-6729">
        <motion.h1 initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="text-5xl md:text-6xl font-bold tracking-tight mb-6 leading-tight" data-id="element-6730">
          From discovery to settlement, Booker runs the full booking workflow.
        </motion.h1>
        <motion.p initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.1
      }} className="text-xl text-textMuted mb-10 max-w-3xl mx-auto leading-relaxed" data-id="element-6731">
          Booker combines venue and artist discovery, outreach, deal tracking,
          contracts, deposits, lineup coordination, and show operations in one
          platform built for live performance.
        </motion.p>
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.2
      }} data-id="element-6732">
          <Link to="/onboarding" className="inline-flex items-center gap-2 px-8 py-4 text-base font-medium text-background bg-primary rounded-lg hover:bg-primaryHover transition-all shadow-[0_0_20px_rgba(245,158,11,0.3)]" data-id="element-6733">
            Start free
            <ChevronRight className="w-4 h-4" data-id="element-6734" />
          </Link>
        </motion.div>
      </section>

      {/* Architecture Diagram (Abstracted) */}
      <section className="max-w-6xl mx-auto px-6 mb-32" data-id="element-6735">
        <div className="bg-surface border border-border rounded-2xl p-8 md:p-12 relative overflow-hidden" data-id="element-6736">
          <div className="absolute top-0 right-0 w-96 h-96 bg-primary/5 rounded-full blur-[100px]" data-id="element-6737"></div>

          <div className="grid md:grid-cols-3 gap-8 relative z-10" data-id="element-6738">
            <div className="bg-background border border-border rounded-xl p-6 text-center" data-id="element-6739">
              <h3 className="font-semibold mb-2" data-id="element-6740">Artists & Bands</h3>
              <p className="text-sm text-textMuted" data-id="element-6741">
                Plan tours, find venues, and manage shows.
              </p>
            </div>
            <div className="flex items-center justify-center" data-id="element-6742">
              <div className="w-full h-px bg-gradient-to-r from-transparent via-primary to-transparent relative" data-id="element-6743">
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 px-4 py-2 bg-primary/10 text-primary text-sm font-bold rounded-full border border-primary/20" data-id="element-6744">
                  Shared Workflow Engine
                </div>
              </div>
            </div>
            <div className="bg-background border border-border rounded-xl p-6 text-center" data-id="element-6745">
              <h3 className="font-semibold mb-2" data-id="element-6746">Venues & Promoters</h3>
              <p className="text-sm text-textMuted" data-id="element-6747">
                Fill dates, discover acts, and program rooms.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Core Modules Grid */}
      <section className="max-w-6xl mx-auto px-6 mb-32" data-id="element-6748">
        <div className="text-center mb-16" data-id="element-6749">
          <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-6750">
            Everything you need to book a show.
          </h2>
          <p className="text-lg text-textMuted" data-id="element-6751">
            One platform replacing spreadsheets, email threads, and PDFs.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-6752">
          {[{
          icon: <Map className="w-6 h-6" data-id="element-6753" />,
          title: 'Discovery & Routing',
          desc: 'Find venues, artists, and markets that actually fit your criteria and availability.'
        }, {
          icon: <MessageSquare className="w-6 h-6" data-id="element-6754" />,
          title: 'Outreach & Inbox',
          desc: 'Manage booking conversations with templates, sequences, and AI assistance.'
        }, {
          icon: <Handshake className="w-6 h-6" data-id="element-6755" />,
          title: 'Deals & Holds',
          desc: 'Track every conversation, hold, offer, and counter in a visual pipeline.'
        }, {
          icon: <FileSignature className="w-6 h-6" data-id="element-6756" />,
          title: 'Contracts & Payments',
          desc: 'Generate docs, collect signatures, request deposits, and monitor settlement.'
        }, {
          icon: <Calendar className="w-6 h-6" data-id="element-6757" />,
          title: 'Lineup & Schedule',
          desc: 'Make sure the right people can actually play the show, then manage logistics.'
        }, {
          icon: <BarChart3 className="w-6 h-6" data-id="element-6758" />,
          title: 'Analytics & Ops',
          desc: 'Understand your route economics, conversion rates, and financial health.'
        }].map((module, i) => <div key={i} className="bg-surface border border-border rounded-xl p-8 hover:border-primary/50 transition-colors" data-id="element-6759">
              <div className="w-12 h-12 rounded-lg bg-primary/10 text-primary flex items-center justify-center mb-6" data-id="element-6760">
                {module.icon}
              </div>
              <h3 className="text-xl font-semibold mb-3" data-id="element-6761">{module.title}</h3>
              <p className="text-textMuted leading-relaxed" data-id="element-6762">{module.desc}</p>
            </div>)}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-3xl mx-auto px-6 text-center" data-id="element-6763">
        <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-6764">
          Ready to upgrade your booking workflow?
        </h2>
        <Link to="/onboarding" className="inline-flex items-center gap-2 px-8 py-4 text-base font-medium text-background bg-primary rounded-lg hover:bg-primaryHover transition-all shadow-[0_0_20px_rgba(245,158,11,0.3)]" data-id="element-6765">
          Start free today
          <ChevronRight className="w-4 h-4" data-id="element-6766" />
        </Link>
      </section>
    </div>;
}